import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import axios from 'axios'
import toast from 'react-hot-toast'
import { handlePermissionError } from '../utils/permissionErrorHandler'

interface ClientService {
  id: string
  serviceNumber: string
  status: string
  startDate: string
  endDate?: string
  currentCycle: number
  totalCycles: number
  // Independent client service fields
  name: string
  description?: string
  serviceType: string
  serviceCategory: string
  priceGbp: string | number
  billingCycleDays?: number
  predictedTimeline?: number
  setupTimeDays?: number
  features?: any
  // Client org relation
  clientOrg: {
    id: string
    name: string
    domain: string
  }
  // Enhanced fields for better service overview
  daysRemaining?: number
  usagePercentage?: number
  nextBillingDate?: string
  isOverdue?: boolean
  overdueDays?: number
}

const Services: React.FC = () => {
  const { user } = useAuthStore()
  
  const [services, setServices] = useState<ClientService[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        console.log('🔍 Fetching services for user:', user?.email)
        console.log('🔍 User roles:', user?.roles)
        
        // Fetch client services based on user role
        const response = await axios.get('/api/services/client-services')
        
        console.log('📊 Services API response:', response.data)
        
        if (response.data.success) {
          setServices(response.data.data || [])
          console.log('✅ Services loaded:', response.data.data?.length || 0)
        } else {
          console.error('❌ Services API failed:', response.data)
          setError(response.data.error || 'Failed to fetch services')
        }
      } catch (error: any) {
        console.error('❌ Error fetching services:', error)
        console.error('❌ Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url
        })
        
        // Handle permission errors specifically
        if (handlePermissionError(error, 'view services')) {
          setError('You don\'t have permission to view services. Please contact your administrator.')
        } else {
          setError(error.response?.data?.error || 'Failed to fetch services')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchServices()
  }, [user])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatPrice = (price: string | number) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    return `£${numPrice.toFixed(2)}`
  }

  const calculateServiceUsage = (service: ClientService) => {
    const startDate = new Date(service.startDate)
    const now = new Date()
    const daysSinceStart = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const cycleDays = service.billingCycleDays || 28
    const currentCycle = Math.floor(daysSinceStart / cycleDays) + 1
    const daysInCurrentCycle = daysSinceStart % cycleDays
    const daysRemaining = cycleDays - daysInCurrentCycle
    // Overall progress across full contract length
    const totalCycles = service.totalCycles || 13
    const totalContractDays = totalCycles * cycleDays
    const completedContractDays = Math.min(((Math.max(0, currentCycle - 1) * cycleDays) + daysInCurrentCycle), totalContractDays)
    const usagePercentage = Math.min(100, Math.max(0, (completedContractDays / totalContractDays) * 100))
    
    const nextBillingDate = new Date(startDate)
    nextBillingDate.setDate(startDate.getDate() + (currentCycle * cycleDays))
    
    const isOverdue = daysRemaining < 0
    const overdueDays = isOverdue ? Math.abs(daysRemaining) : 0
    
    return {
      daysRemaining: Math.max(0, daysRemaining),
      usagePercentage,
      nextBillingDate: nextBillingDate.toISOString(),
      isOverdue,
      overdueDays,
      currentCycle: Math.min(currentCycle, service.totalCycles)
    }
  }

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'active': 'bg-green-100 text-green-800',
      'suspended': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800',
      'expired': 'bg-gray-100 text-gray-800'
    }
    return statusColors[status] || 'bg-gray-100 text-gray-800'
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading services...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-red-900">Error loading services</h3>
          <p className="mt-2 text-sm text-red-500">{error}</p>
          <button 
            className="mt-4 btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Services</h1>
          <p className="mt-2 text-sm text-gray-700">
            {user?.roles.includes('client') 
              ? 'Your active service subscriptions' 
              : 'All client service subscriptions'
            }
          </p>
        </div>
      </div>
      
      {/* Billing Overview Summary */}
      {services.length > 0 && user?.roles.includes('client') && (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="card text-center">
            <h3 className="text-lg font-medium text-gray-900">Total Monthly Cost</h3>
            <p className="mt-2 text-3xl font-bold text-green-600">
              £{services.reduce((sum, service) => sum + parseFloat(service.priceGbp), 0).toFixed(2)}
            </p>
            <p className="mt-1 text-sm text-gray-500">Active subscriptions</p>
          </div>
          
          <div className="card text-center">
            <h3 className="text-lg font-medium text-gray-900">Next Payment</h3>
            <p className="mt-2 text-3xl font-bold text-blue-600">
              {(() => {
                const nextBilling = services
                  .map(service => calculateServiceUsage(service))
                  .sort((a, b) => new Date(a.nextBillingDate).getTime() - new Date(b.nextBillingDate).getTime())[0]
                return nextBilling ? formatDate(nextBilling.nextBillingDate) : 'N/A'
              })()}
            </p>
            <p className="mt-1 text-sm text-gray-500">Earliest due date</p>
          </div>
          
          <div className="card text-center">
            <h3 className="text-lg font-medium text-gray-900">Services Status</h3>
            <p className="mt-2 text-3xl font-bold text-purple-600">
              {services.filter(service => !calculateServiceUsage(service).isOverdue).length}/{services.length}
            </p>
            <p className="mt-1 text-sm text-gray-500">Up to date</p>
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <div className="mt-8 card">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">No services found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {user?.roles.includes('client') 
                ? 'You don\'t have any active service subscriptions yet.' 
                : 'No client services have been created yet.'
              }
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {services.map((service) => (
            <div key={service.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {service.name}
                    </h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(service.status)}`}>
                      {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </span>
                  </div>
                  
                  <p className="mt-1 text-sm text-gray-600">
                    {service.description}
                  </p>
                  
                  {/* Service Usage Progress */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Billing Cycle Progress</span>
                      <span>{calculateServiceUsage(service).currentCycle} of {service.totalCycles}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                      <div 
                        className={`h-3 rounded-full transition-all duration-300 ${
                          calculateServiceUsage(service).isOverdue 
                            ? 'bg-red-500' 
                            : calculateServiceUsage(service).daysRemaining <= 7 
                              ? 'bg-yellow-500' 
                              : 'bg-blue-500'
                        }`}
                        style={{ width: `${calculateServiceUsage(service).usagePercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Service Number</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{service.serviceNumber}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Price</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatPrice(service.priceGbp)}/{service.billingCycleDays || 'N/A'} days
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Service Type</dt>
                      <dd className="mt-1 text-sm text-gray-900">{service.serviceType}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Category</dt>
                      <dd className="mt-1 text-sm text-gray-900">{service.serviceCategory}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Started</dt>
                      <dd className="mt-1 text-sm text-gray-900">{formatDate(service.startDate)}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Next Billing</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatDate(calculateServiceUsage(service).nextBillingDate)}
                      </dd>
                    </div>
                  </div>

                  {/* Billing Status */}
                  <div className="mt-4 p-3 rounded-lg border-l-4 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Billing Status</h4>
                        {calculateServiceUsage(service).isOverdue ? (
                          <p className="text-sm text-red-600">
                            Overdue by {calculateServiceUsage(service).overdueDays} days
                          </p>
                        ) : calculateServiceUsage(service).daysRemaining <= 7 ? (
                          <p className="text-sm text-yellow-600">
                            Due in {calculateServiceUsage(service).daysRemaining} days
                          </p>
                        ) : (
                          <p className="text-sm text-green-600">
                            {calculateServiceUsage(service).daysRemaining} days remaining
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          {formatPrice(service.priceGbp)}
                        </p>
                        <p className="text-xs text-gray-500">Next payment</p>
                      </div>
                    </div>
                  </div>
                  
                  {!user?.roles.includes('client') && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <dt className="text-sm font-medium text-gray-500">Client Organization</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {service.clientOrg.name} ({service.clientOrg.domain})
                      </dd>
                    </div>
                  )}
                  
                  {service.features && Object.keys(service.features).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <dt className="text-sm font-medium text-gray-500">Features</dt>
                      <dd className="mt-2 text-sm text-gray-900">
                        <ul className="list-disc list-inside space-y-1">
                          {Object.entries(service.features).map(([key, value]) => (
                            <li key={key}>
                              <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>{' '}
                              {Array.isArray(value) ? value.join(', ') : String(value)}
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Services
